import {
    type ErrorEnum,
    TaskResultMessageTypeEnum,
    VideoStatus,
    type ZipVideoRendersResponse,
} from 'js/src/client';
import type { HTTP_STATUS_CODES } from 'js/src/libs/consts';
import { videoDownload } from 'js/src/libs/download-utils';
import UserPrintableError from 'js/src/libs/user-printable-error';
import WebSocketClient from 'js/src/libs/websocket-client';

type Opts = {
    finishedCallback: (url: string) => void;
    failCallback: (error: Error) => void;
    renderingStartedCallback: () => void;
    videoId: number;
};

export class DownloadLockedError extends Error {
    code: ErrorEnum;
    constructor(code: ErrorEnum) {
        super();
        this.code = code;
    }
}

export class DownloadHttpError extends Error {
    status: HTTP_STATUS_CODES;
    constructor(status: HTTP_STATUS_CODES) {
        super();
        this.status = status;
    }
}

class VideoDownloader {
    opts: Opts;

    constructor(opts: Opts) {
        this.opts = opts;
    }

    download() {
        videoDownload(this.opts.videoId)
            .then((response) => {
                const { videoStatus, taskId, url } = response;
                switch (videoStatus) {
                    case VideoStatus.DRAFT:
                    case VideoStatus.QUEUED:
                    case VideoStatus.PROCESSING: {
                        // we should open the rendering modal
                        this._callRenderingStartedCallback();
                        break;
                    }
                    case VideoStatus.PUBLISHED: {
                        // we should be able to download via the url or the task_id
                        if (url) {
                            this._callFinishedCallback(url);
                        } else if (taskId) {
                            this._pollZipTask(taskId);
                        } else {
                            this._callFailCallback(
                                new UserPrintableError(
                                    'The video is published, but we can not find a downloadable version.',
                                ),
                            );
                        }
                        break;
                    }
                    case VideoStatus.FAILED: {
                        // we should return an error
                        this._callFailCallback(
                            new UserPrintableError(
                                'This video failed to render. Our team is investigating and will have your video ready within 24 hours.',
                            ),
                        );
                        break;
                    }
                }
            })
            .catch((err) => {
                this._callFailCallback(err);
            });
    }

    async _pollZipTask(taskId: string) {
        const ws = WebSocketClient.getInstance();
        try {
            const response = await ws.waitForMessage<ZipVideoRendersResponse>(
                taskId,
                TaskResultMessageTypeEnum.TASK_RESULT,
                'zip_video_renders',
            );
            this._callFinishedCallback(response.zip_url);
        } catch {
            this._callFailCallback(
                new Error(
                    'Your video finished but could not be downloaded, try downloading your video from the dashboard',
                ),
            );
        }
    }

    _callFinishedCallback(url: string) {
        this.opts.finishedCallback(url);
    }

    _callFailCallback(error: Error) {
        this.opts.failCallback(error);
    }

    _callRenderingStartedCallback() {
        this.opts.renderingStartedCallback();
    }
}

export function downloadVideoAsPromise({
    videoId,
    sharePageUrl,
}: {
    videoId: number;
    sharePageUrl?: string;
}): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        const videoDownloader = new VideoDownloader({
            videoId,
            finishedCallback: () => {
                resolve();
            },
            failCallback: (error: Error) => {
                reject(error);
            },
            renderingStartedCallback: () => {
                if (sharePageUrl) {
                    window.open(sharePageUrl);
                }
                resolve();
            },
        });
        videoDownloader.download();
    });
}

export default VideoDownloader;
